import { useEffect, useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, Smartphone } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { RESTAURANT } from "@/data/menu";

const links = [
  { to: "/", label: "Home", testId: NAV.homeLink, mobileTestId: NAV.mobileHomeLink },
  { to: "/menu", label: "Menu", testId: NAV.menuLink, mobileTestId: NAV.mobileMenuLink },
  { to: "/about", label: "About", testId: NAV.aboutLink, mobileTestId: NAV.mobileAboutLink },
  { to: "/contact-us", label: "Contact", testId: NAV.contactLink, mobileTestId: NAV.mobileContactLink },
];

const logo = "/images/logo.png";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === "/" || location.pathname === "/menu";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
  }, [open]);

  const scrollToAppSection = () => {
    const el = document.getElementById("app");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleAppClick = (e) => {
    e.preventDefault();
    setOpen(false);
    if (location.pathname === "/") {
      scrollToAppSection();
    } else {
      navigate("/#app");
      setTimeout(scrollToAppSection, 150);
    }
  };

  const baseLink = "relative text-sm tracking-wide font-medium transition-colors duration-200";
  const inactive = "text-cream/70 hover:text-cream";
  const active = "text-cream";

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        isHome && !scrolled
          ? "bg-transparent border-b border-transparent"
          : "bg-brand shadow-[0_2px_12px_0_rgba(0,0,0,0.2)] border-b border-white/10"
      }`}
    >
      <nav className="container-x flex items-center justify-between h-20">
        {/* Logo */}
        <Link
          to="/"
          data-testid={NAV.brand}
          aria-label="Giridhar Veg Restaurant — Home"
          className="flex items-center group"
        >
          <img
            src={logo}
            alt="Giridhar"
            className="h-10 w-auto object-contain group-hover:scale-105 transition-transform duration-200"
          />
        </Link>

        {/* Desktop Links */}
        <div className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              end={l.to === "/"}
              className={({ isActive }) => `${baseLink} ${isActive ? active : inactive}`}
            >
              {({ isActive }) => (
                <span className="inline-flex flex-col items-center">
                  {l.label}
                  <span
                    className={`mt-1 block h-[1.5px] rounded-full bg-cream transition-all duration-300 ${
                      isActive ? "w-5 opacity-100" : "w-0 opacity-0"
                    }`}
                  />
                </span>
              )}
            </NavLink>
          ))}
        </div>

        {/* Desktop CTA */}
        <div className="hidden md:flex items-center gap-3">
          
            <a href="/#app"
            onClick={handleAppClick}
            data-testid={NAV.callBtn}
            className="inline-flex items-center gap-2 rounded-full bg-cream text-brand hover:bg-cream-deep px-5 py-2.5 text-sm font-medium tracking-wide transition-colors shadow-sm"
          >
            <Smartphone className="w-4 h-4" strokeWidth={2} />
            Order on App
          </a>
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setOpen((o) => !o)}
          data-testid={NAV.mobileToggle}
          aria-label="Toggle menu"
          aria-expanded={open}
          className="md:hidden p-2 rounded-full text-cream hover:bg-white/10 transition"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {/* Mobile Drawer */}
      <div
        className={`md:hidden overflow-hidden transition-[max-height,opacity] duration-300 ${
          open ? "max-h-[420px] opacity-100" : "max-h-0 opacity-0"
        } bg-brand border-t border-white/10`}
      >
        <div className="container-x py-6 flex flex-col gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={l.mobileTestId}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `text-base px-3 py-3 rounded-xl transition-colors ${
                  isActive
                    ? "text-cream bg-white/10"
                    : "text-cream/70 hover:bg-white/10 hover:text-cream"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          
            <a href="/#app"
            onClick={handleAppClick}
            className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-cream text-brand px-5 py-3 text-sm font-medium tracking-wide"
          >
            <Smartphone className="w-4 h-4" /> Order on App
          </a>
          
            <a href={`tel:${RESTAURANT.phoneRaw}`}
            onClick={() => setOpen(false)}
            className="inline-flex items-center justify-center gap-2 rounded-full border border-cream/30 text-cream px-5 py-3 text-sm font-medium tracking-wide"
          >
            <Phone className="w-4 h-4" /> {RESTAURANT.phone}
          </a>
        </div>
      </div>
    </header>
  );
}